import React from "react";
import DohopHeader from "./DohopHeader";
import DohopSearchForm from "./DohopSearchForm";

const DohopHomepage: React.FC = () => {
    return (
        <div className="min-h-screen bg-dohop-gray">
            <DohopHeader />

            {/* Hero section */}
            <div className="relative bg-gradient-to-br from-dohop-blue via-dohop-blue-dark to-dohop-navy py-20 md:py-32">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                    <svg
                        className="w-full h-full"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <pattern
                            id="hero-pattern"
                            x="0"
                            y="0"
                            width="40"
                            height="40"
                            patternUnits="userSpaceOnUse"
                        >
                            <circle cx="20" cy="20" r="1" fill="white" />
                        </pattern>
                        <rect
                            width="100%"
                            height="100%"
                            fill="url(#hero-pattern)"
                        />
                    </svg>
                </div>

                {/* Hero content */}
                <div className="relative max-w-7xl mx-auto px-4 text-center">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                        Find Your Perfect Flight
                    </h1>
                    <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
                        Compare prices across hundreds of airlines and book with
                        confidence with Dohop Protection
                    </p>

                    {/* Trust badges */}
                    <div className="flex flex-wrap justify-center gap-6 mb-12">
                        <div className="flex items-center gap-2 text-white/90">
                            <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <span className="text-sm font-medium">
                                Guaranteed Connections
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-white/90">
                            <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <span className="text-sm font-medium">
                                Best Price Guarantee
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-white/90">
                            <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                                <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                            </svg>
                            <span className="text-sm font-medium">
                                100+ Airlines
                            </span>
                        </div>
                    </div>
                </div>

                {/* Search form positioned at bottom of hero */}
                <div className="relative max-w-7xl mx-auto px-4 -mb-24">
                    <DohopSearchForm />
                </div>
            </div>

            {/* Spacer for search form overlap */}
            <div className="h-32"></div>

            {/* Features section */}
            <div className="max-w-7xl mx-auto px-4 py-16">
                <h2 className="text-2xl font-bold text-dohop-navy text-center mb-12">
                    Why Book with Dohop?
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Feature 1 */}
                    <div className="bg-white rounded-xl p-6 shadow-md">
                        <div className="w-12 h-12 bg-dohop-ice rounded-lg flex items-center justify-center mb-4">
                            <svg
                                className="w-6 h-6 text-dohop-blue"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-dohop-navy mb-2">
                            Dohop Protection
                        </h3>
                        <p className="text-gray-600">
                            If you miss a connection due to a delay, we'll get
                            you to your destination at no extra cost.
                        </p>
                    </div>

                    {/* Feature 2 */}
                    <div className="bg-white rounded-xl p-6 shadow-md">
                        <div className="w-12 h-12 bg-dohop-ice rounded-lg flex items-center justify-center mb-4">
                            <svg
                                className="w-6 h-6 text-dohop-blue"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-dohop-navy mb-2">
                            Best Prices
                        </h3>
                        <p className="text-gray-600">
                            We search across all major airlines to find you the
                            best deals on flights worldwide.
                        </p>
                    </div>

                    {/* Feature 3 */}
                    <div className="bg-white rounded-xl p-6 shadow-md">
                        <div className="w-12 h-12 bg-dohop-ice rounded-lg flex items-center justify-center mb-4">
                            <svg
                                className="w-6 h-6 text-dohop-blue"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"
                                />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-dohop-navy mb-2">
                            Icelandic Hospitality
                        </h3>
                        <p className="text-gray-600">
                            Founded in Reykjavik, we bring Nordic reliability
                            and transparency to flight booking.
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-dohop-navy text-white py-8">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <p className="text-white/60 text-sm">
                        Demo for Movmo Express Checkout Integration
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default DohopHomepage;
